/**
 * Cryptographic Utilities
 * Handles key derivation (PBKDF2), encryption (AES-GCM), and key wrapping
 */

/**
 * Derive a vault key from passphrase using PBKDF2
 * @param {string} passphrase - User passphrase
 * @param {string} salt - Salt (base64 encoded, will generate if not provided)
 * @param {number} iterations - PBKDF2 iterations (default: 100000)
 * @param {boolean} extractable - Whether the key should be extractable (default: true, needed for wrapping)
 * @returns {Promise<{key: CryptoKey, salt: string}>} - Derived key and salt
 */
export async function deriveVaultKey(passphrase, salt = null, iterations = 100000, extractable = true) {
  // Generate salt if not provided
  let saltBuffer;
  if (salt) {
    saltBuffer = base64ToArrayBuffer(salt);
  } else {
    saltBuffer = crypto.getRandomValues(new Uint8Array(16));
  }

  // Import passphrase as key material
  const encoder = new TextEncoder();
  const passphraseKey = await crypto.subtle.importKey(
    'raw',
    encoder.encode(passphrase),
    'PBKDF2',
    false,
    ['deriveBits', 'deriveKey']
  );

  // Derive key using PBKDF2
  // Note: extractable must be true if we want to wrap this key with wrapKey()
  // The key is still secure in memory - extractability only matters if someone tries to export it
  const derivedKey = await crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: saltBuffer,
      iterations,
      hash: 'SHA-256',
    },
    passphraseKey,
    { name: 'AES-GCM', length: 256 },
    extractable,
    ['encrypt', 'decrypt', 'wrapKey'] // Added 'wrapKey' to key usages for clarity
  );

  return {
    key: derivedKey,
    salt: arrayBufferToBase64(saltBuffer),
  };
}

/**
 * Generate a random device key
 * @returns {Promise<{key: CryptoKey, raw: ArrayBuffer}>} - Device key and raw bytes
 */
export async function generateDeviceKey() {
  const key = await crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    true, // extractable
    ['encrypt', 'decrypt', 'wrapKey', 'unwrapKey']
  );

  const rawKey = await crypto.subtle.exportKey('raw', key);

  return {
    key,
    raw: rawKey,
  };
}

/**
 * Wrap (encrypt) a key with another key
 * @param {CryptoKey} keyToWrap - Key to wrap (vault key)
 * @param {CryptoKey} wrappingKey - Key to wrap with (device key)
 * @returns {Promise<string>} - Wrapped key (base64 encoded)
 */
export async function wrapKey(keyToWrap, wrappingKey) {
  const iv = crypto.getRandomValues(new Uint8Array(12));

  const wrappedKey = await crypto.subtle.wrapKey(
    'raw',
    keyToWrap,
    wrappingKey,
    {
      name: 'AES-GCM',
      iv,
    }
  );

  // Combine IV and wrapped key
  const combined = new Uint8Array(iv.length + wrappedKey.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(wrappedKey), iv.length);

  return arrayBufferToBase64(combined.buffer);
}

/**
 * Unwrap (decrypt) a key
 * @param {string} wrappedKey - Wrapped key (base64 encoded)
 * @param {CryptoKey} unwrappingKey - Key to unwrap with (device key)
 * @returns {Promise<CryptoKey>} - Unwrapped key (vault key)
 */
export async function unwrapKey(wrappedKey, unwrappingKey) {
  const combined = base64ToArrayBuffer(wrappedKey);
  const iv = combined.slice(0, 12);
  const wrapped = combined.slice(12);

  const unwrappedKey = await crypto.subtle.unwrapKey(
    'raw',
    wrapped,
    unwrappingKey,
    {
      name: 'AES-GCM',
      iv,
    },
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );

  return unwrappedKey;
}

/**
 * Encrypt data with a key
 * @param {string} data - Data to encrypt (JSON string)
 * @param {CryptoKey} key - Encryption key (vault key)
 * @returns {Promise<string>} - Encrypted data (base64 encoded, includes IV)
 */
export async function encryptData(data, key) {
  const encoder = new TextEncoder();
  const dataBuffer = encoder.encode(data);
  const iv = crypto.getRandomValues(new Uint8Array(12));

  const encrypted = await crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv,
    },
    key,
    dataBuffer
  );

  // Combine IV and encrypted data
  const combined = new Uint8Array(iv.length + encrypted.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(encrypted), iv.length);

  return arrayBufferToBase64(combined.buffer);
}

/**
 * Decrypt data with a key
 * @param {string} encryptedData - Encrypted data (base64 encoded, includes IV)
 * @param {CryptoKey} key - Decryption key (vault key)
 * @returns {Promise<string>} - Decrypted data (JSON string)
 */
export async function decryptData(encryptedData, key) {
  const combined = base64ToArrayBuffer(encryptedData);
  const iv = combined.slice(0, 12);
  const encrypted = combined.slice(12);

  const decrypted = await crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv,
    },
    key,
    encrypted
  );

  const decoder = new TextDecoder();
  return decoder.decode(decrypted);
}

/**
 * Encrypt device key with passphrase-derived key (for first-time storage)
 * @param {ArrayBuffer} deviceKeyRaw - Raw device key bytes
 * @param {CryptoKey} passphraseKey - Passphrase-derived key
 * @returns {Promise<string>} - Encrypted device key (base64)
 */
export async function encryptDeviceKey(deviceKeyRaw, passphraseKey) {
  // First import the device key as a CryptoKey
  const deviceKey = await crypto.subtle.importKey(
    'raw',
    deviceKeyRaw,
    { name: 'AES-GCM', length: 256 },
    false,
    ['wrapKey', 'unwrapKey']
  );

  // Wrap the device key with the passphrase key
  return await wrapKey(deviceKey, passphraseKey);
}

/**
 * Decrypt device key with passphrase-derived key
 * @param {string} encryptedDeviceKey - Encrypted device key (base64)
 * @param {CryptoKey} passphraseKey - Passphrase-derived key
 * @returns {Promise<CryptoKey>} - Decrypted device key
 */
export async function decryptDeviceKey(encryptedDeviceKey, passphraseKey) {
  return await unwrapKey(encryptedDeviceKey, passphraseKey);
}

/**
 * Convert ArrayBuffer to base64 string
 */
function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Convert base64 string to ArrayBuffer
 */
function base64ToArrayBuffer(base64) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}
